import { useEffect, useState } from "react";
import { usePoll } from "@/hooks/usePoll";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
  Building2, Users, CalendarCheck, XCircle, CheckCircle2, Lock, ShieldAlert, Loader2, TrendingUp,
  Briefcase, Home, ClipboardList, AlertTriangle, ChevronRight, UserCheck, MapPin, CalendarClock, Clock, Hourglass,
} from "lucide-react";
import { toast } from "sonner";
import { WorkdayCard } from "@/components/WorkdayCard";
import { idleInfo } from "@/lib/idle";
import { StorageCard } from "@/components/StorageCard";

const fmtHM = (h) => {
  if (h == null) return "—";
  const m = Math.round(Number(h) * 60);
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${hh}:${String(mm).padStart(2, "0")}`;
};

function Kpi({ icon: Icon, label, value, tint, testid, onClick }) {
  return (
    <Card
      data-testid={testid}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      className={`p-5 border-slate-200 group transition ${onClick ? "cursor-pointer hover:border-brand/40 hover:shadow-md" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
          <p className="font-display text-3xl font-extrabold text-slate-900 mt-1">{value ?? "—"}</p>
        </div>
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tint}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {onClick && (
        <p className="mt-3 text-[11px] font-semibold text-slate-400 group-hover:text-brand flex items-center gap-0.5 transition">
          View <ChevronRight className="h-3 w-3" />
        </p>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const { user, setupMfa, enableMfa } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaData, setMfaData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    client.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  usePoll(() => client.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}), 20000);

  const [idleCount, setIdleCount] = useState(0);
  const loadIdle = () => client.get("/attendance/live").then((r) => {
    setIdleCount((r.data.staff || []).filter((s) => idleInfo(s.pingAt).isIdle).length);
  }).catch(() => {});
  useEffect(() => { loadIdle(); }, []);
  usePoll(loadIdle, 20000);

  const startMfa = async () => {
    try {
      const d = await setupMfa();
      setMfaData(d);
      setMfaOpen(true);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  const confirmMfa = async () => {
    setBusy(true);
    try {
      await enableMfa(code);
      toast.success(t("toast.mfaEnabled","MFA enabled"));
      setMfaOpen(false);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const ownerNeedsMfa = user?.role === "owner" && !user?.mfaEnabled;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h2 className="font-display text-2xl font-bold text-slate-900" data-testid="dashboard-user-name">{user?.displayName}</h2>
        <p className="text-sm text-slate-500 capitalize">{user?.role} · iClean NW Operations</p>
      </div>

      {user?.role && user.role !== "cleaner" && <WorkdayCard canDelete={user.role === "owner" || user.role === "admin"} />}

      {stats?.clockedInNow != null && (
        <div data-testid="dashboard-live-now">
          <div className="flex items-center gap-2 mb-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t("dash.liveNow", "Live now")}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi testid="kpi-clocked-in-now" icon={UserCheck} label={t("dash.kpi.clockedIn", "Clocked in now")} value={stats?.clockedInNow} tint="bg-emerald-100 text-emerald-700" onClick={() => navigate("/whos-clocked-in")} />
            <Kpi testid="kpi-connect-sites-active" icon={MapPin} label={t("dash.kpi.connectActive", "Connect sites active")} value={stats?.connectSitesActive} tint="bg-orange-100 text-orange-700" onClick={() => navigate("/attendance")} />
            <Kpi testid="kpi-active-jobs-now" icon={Briefcase} label={t("dash.kpi.activeJobs", "Active jobs running")} value={stats?.activeJobsNow} tint="bg-blue-100 text-blue-700" onClick={() => navigate("/field-map")} />
            <Kpi testid="kpi-scheduled-jobs-today" icon={CalendarClock} label={t("dash.kpi.scheduledJobs", "Scheduled jobs today")} value={stats?.scheduledJobsToday} tint="bg-indigo-100 text-indigo-700" onClick={() => navigate("/jobs")} />
            <Kpi testid="kpi-completed-jobs-today" icon={CheckCircle2} label={t("dash.kpi.completedJobs", "Completed jobs today")} value={stats?.completedJobsToday} tint="bg-green-100 text-green-700" onClick={() => navigate("/jobs")} />
            <Kpi testid="kpi-hours-today" icon={Clock} label={t("dash.kpi.hoursToday", "Hours worked today")} value={fmtHM(stats?.hoursWorkedToday)} tint="bg-teal-100 text-teal-700" />
            <Kpi testid="kpi-outside-geofence-today" icon={ShieldAlert} label={t("dash.kpi.outsideGeofence", "Outside geofence")} value={stats?.outsideGeofenceToday} tint={stats?.outsideGeofenceToday ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"} onClick={() => navigate("/attendance")} />
            <Kpi testid="kpi-idle-staff" icon={Hourglass} label={t("dash.kpi.idleStaff", "Idle staff")} value={idleCount} tint={idleCount ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"} onClick={() => navigate("/field-map?idle=1")} />
          </div>
        </div>
      )}

      {ownerNeedsMfa && (
        <Card data-testid="mfa-setup-banner" className="p-4 border-amber-300 bg-amber-50 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-900 text-sm">{t("dash.mfa.title","Owner MFA is required")}</p>
              <p className="text-xs text-amber-700">{t("dash.mfa.desc","Protect the payroll vault by enabling two-factor authentication.")}</p>
            </div>
          </div>
          <Button data-testid="enable-mfa-btn" onClick={startMfa} className="bg-amber-600 hover:bg-amber-700">
            <Lock className="h-4 w-4 mr-2" /> {t("dash.mfa.enable","Enable MFA")}
          </Button>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-sites" icon={Building2} label={t("dash.kpi.sites","Sites")} value={stats?.sites} tint="bg-orange-100 text-orange-700" onClick={() => navigate("/sites")} />
        <Kpi testid="kpi-staff" icon={Users} label={t("dash.kpi.staff","Staff")} value={stats?.staff} tint="bg-teal-100 text-teal-700" onClick={() => navigate("/staff")} />
        <Kpi testid="kpi-active-shifts" icon={CalendarCheck} label={t("dash.kpi.active","Active Shifts")} value={stats?.activeShifts} tint="bg-emerald-100 text-emerald-700" onClick={() => navigate("/schedule")} />
        <Kpi testid="kpi-completed-shifts" icon={CheckCircle2} label={t("dash.kpi.completed","Completed")} value={stats?.completedShifts} tint="bg-slate-100 text-slate-700" onClick={() => navigate("/schedule")} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-cancelled-shifts" icon={XCircle} label={t("dash.kpi.cancelled","Cancelled (retained)")} value={stats?.cancelledShifts} tint="bg-purple-100 text-purple-700" onClick={() => navigate("/schedule")} />
        {user?.role === "owner" && (
          <Kpi testid="kpi-outstanding-payments" icon={TrendingUp} label={t("dash.kpi.outstanding","Outstanding Pay")} value={stats?.outstandingPayments} tint="bg-amber-100 text-amber-700" onClick={() => navigate("/payroll")} />
        )}
      </div>

      {(stats?.clients != null || stats?.jobs != null || stats?.openIssues != null) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-ops-grid">
          {stats?.clients != null && <Kpi testid="kpi-clients" icon={Briefcase} label={t("dash.kpi.clients","Clients")} value={stats?.clients} tint="bg-indigo-100 text-indigo-700" onClick={() => navigate("/clients")} />}
          {stats?.properties != null && <Kpi testid="kpi-properties" icon={Home} label={t("dash.kpi.properties","Properties")} value={stats?.properties} tint="bg-cyan-100 text-cyan-700" onClick={() => navigate("/properties")} />}
          {stats?.jobs != null && <Kpi testid="kpi-jobs" icon={ClipboardList} label={t("dash.kpi.jobs","Jobs")} value={stats?.jobs} tint="bg-blue-100 text-blue-700" onClick={() => navigate("/jobs")} />}
          <Kpi testid="kpi-open-issues" icon={AlertTriangle} label={t("dash.kpi.issues","Open issues")} value={stats?.openIssues} tint="bg-red-100 text-red-700" onClick={() => navigate("/issues")} />
        </div>
      )}

      {(user?.role === "owner" || user?.role === "admin") && <StorageCard />}

      <Dialog open={mfaOpen} onOpenChange={setMfaOpen}>
        <DialogContent data-testid="mfa-setup-dialog">
          <DialogHeader><DialogTitle>Enable two-factor authentication</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Add this secret to your authenticator app (Google Authenticator, Authy), then enter the 6-digit code.</p>
            {mfaData && (
              <div className="p-3 bg-slate-100 rounded-lg font-mono text-sm break-all" data-testid="mfa-secret">{mfaData.secret}</div>
            )}
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="mfa-enable-input">
                <InputOTPGroup>{[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
              </InputOTP>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={confirmMfa} disabled={busy || code.length < 6} data-testid="mfa-enable-confirm-btn" className="bg-brand hover:bg-brand-dark">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & enable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
